/**
 * CustomerNotesTab — free-text notes for a single customer (TASK-01c).
 * Uses the notes service directly. Shows an add-note box at the top and a
 * reverse-chronological list (author + timestamp) with delete.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { Check, Edit2, MessageSquare, Pin, PinOff, Send, Trash2, User, X } from 'lucide-react'
import { getNoteService } from '@/services/notes'
import { toast } from '@/store/toastStore'
import type { CustomerNote } from '@/types/note.types'
import type { IndividualCustomer } from '@/types/customer'
import { useAuth } from '@/contexts/AuthContext'

interface CustomerNotesTabProps {
  customer: IndividualCustomer
  /** When true (set by the Overview Quick Action), focus the note composer on mount. */
  autoCreate?: boolean
  /** Called once the auto-focus has been handled, so the parent can clear its flag. */
  onAutoCreateHandled?: () => void
}

function formatTimestamp(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function sortNotes(notes: CustomerNote[]): CustomerNote[] {
  return [...notes].sort((a, b) => {
    if (a.isPinned && !b.isPinned) return -1
    if (!a.isPinned && b.isPinned) return 1
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  })
}

export default function CustomerNotesTab({
  customer,
  autoCreate,
  onAutoCreateHandled,
}: CustomerNotesTabProps) {
  const { hasPermission } = useAuth()
  const canCreate = hasPermission('txh.client.note.create')
  const canDelete = hasPermission('txh.client.note.delete')
  const [notes, setNotes] = useState<CustomerNote[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [draft, setDraft] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null)
  const [editDraft, setEditDraft] = useState('')
  const [savingEdit, setSavingEdit] = useState(false)
  const [pinningNoteId, setPinningNoteId] = useState<string | null>(null)
  const composerRef = useRef<HTMLTextAreaElement>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setLoadError('')
    try {
      const items = await getNoteService().listForCustomer(customer.id)
      setNotes(sortNotes(items))
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Please try again.')
      toast.error('Could not load notes', err instanceof Error ? err.message : 'Please try again.')
    } finally {
      setLoading(false)
    }
  }, [customer.id])

  useEffect(() => {
    load()
  }, [load])

  // Honor the Overview "Add Note" Quick Action: focus the composer once.
  useEffect(() => {
    if (autoCreate && canCreate) {
      composerRef.current?.focus()
      onAutoCreateHandled?.()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoCreate])

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!canCreate) return
    const body = draft.trim()
    if (!body) return
    setSubmitting(true)
    try {
      const created = await getNoteService().create(customer.id, body)
      setNotes(prev => sortNotes([created, ...prev]))
      setDraft('')
      toast.success('Note added')
    } catch (err) {
      toast.error('Could not add note', err instanceof Error ? err.message : 'Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  const handleTogglePin = async (note: CustomerNote) => {
    setPinningNoteId(note.id)
    try {
      const updated = await getNoteService().update(customer.id, note.id, {
        isPinned: !note.isPinned,
      })
      setNotes(prev => sortNotes(prev.map(n => (n.id === note.id ? updated : n))))
      toast.success(note.isPinned ? 'Note unpinned' : 'Note pinned to top')
    } catch (err) {
      toast.error('Could not update note', err instanceof Error ? err.message : 'Please try again.')
    } finally {
      setPinningNoteId(null)
    }
  }

  const handleStartEdit = (note: CustomerNote) => {
    setEditingNoteId(note.id)
    setEditDraft(note.body)
  }

  const handleCancelEdit = () => {
    setEditingNoteId(null)
    setEditDraft('')
  }

  const handleSaveEdit = async (note: CustomerNote) => {
    const trimmed = editDraft.trim()
    if (!trimmed) return
    setSavingEdit(true)
    try {
      const updated = await getNoteService().update(customer.id, note.id, { body: trimmed })
      setNotes(prev => prev.map(n => (n.id === note.id ? updated : n)))
      setEditingNoteId(null)
      toast.success('Note updated')
    } catch (err) {
      toast.error('Could not update note', err instanceof Error ? err.message : 'Please try again.')
    } finally {
      setSavingEdit(false)
    }
  }

  const handleDelete = async (note: CustomerNote) => {
    if (!canDelete) return
    if (!window.confirm('Delete this note?')) return
    try {
      await getNoteService().remove(customer.id, note.id)
      setNotes(prev => prev.filter(n => n.id !== note.id))
      toast.success('Note deleted')
    } catch (err) {
      toast.error('Could not delete note', err instanceof Error ? err.message : 'Please try again.')
    }
  }

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-text-primary">Notes</h3>

      {/* Add note box */}
      {canCreate && (
        <form onSubmit={handleAdd} className="card card-elevated p-4 space-y-3">
          <textarea
            ref={composerRef}
            value={draft}
            onChange={e => setDraft(e.target.value)}
            placeholder="Add a note about this client…"
            rows={3}
            className="input input-bordered w-full"
            disabled={submitting}
          />
          <div className="flex justify-end">
            <button
              type="submit"
              className="btn btn-primary btn-sm gap-2"
              disabled={submitting || !draft.trim()}
            >
              {submitting ? (
                <>
                  <span className="loading loading-spinner loading-sm" /> Saving…
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Add note
                </>
              )}
            </button>
          </div>
        </form>
      )}

      {/* Notes list */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="card card-elevated p-4">
              <div className="skeleton h-4 w-3/4 rounded mb-2" />
              <div className="skeleton h-3 w-1/3 rounded" />
            </div>
          ))}
        </div>
      ) : loadError ? (
        <div role="alert" className="card card-elevated p-6 space-y-3">
          <p className="text-red-600 dark:text-red-400">Could not load notes: {loadError}</p>
          <button type="button" className="btn btn-outline" onClick={load}>
            Retry
          </button>
        </div>
      ) : notes.length === 0 ? (
        <div className="card card-elevated p-12 text-center">
          <div className="w-16 h-16 rounded-full bg-surface-overlay flex items-center justify-center mx-auto mb-4">
            <MessageSquare className="w-8 h-8 text-text-tertiary" />
          </div>
          <p className="text-lg font-medium text-text-primary mb-2">No notes yet</p>
          <p className="text-text-secondary">Notes you add about this client will appear here.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {notes.map(note => {
            const isEditing = editingNoteId === note.id
            const isPinning = pinningNoteId === note.id
            const isEdited = Boolean(note.updatedAt && note.updatedAt !== note.createdAt)

            return (
              <div
                key={note.id}
                className={`card card-elevated p-4 transition-all ${
                  note.isPinned
                    ? 'border-amber-500/40 dark:border-amber-500/30 bg-amber-50/20 dark:bg-amber-950/10'
                    : ''
                }`}
              >
                {note.isPinned && (
                  <div className="flex items-center gap-1 text-xs font-medium text-amber-600 dark:text-amber-400 mb-2">
                    <Pin className="w-3.5 h-3.5 fill-current" />
                    <span>Pinned Note</span>
                  </div>
                )}

                <div className="flex items-start justify-between gap-3">
                  {isEditing ? (
                    <div className="flex-1 space-y-2">
                      <textarea
                        value={editDraft}
                        onChange={e => setEditDraft(e.target.value)}
                        rows={3}
                        className="input input-bordered w-full text-sm"
                        disabled={savingEdit}
                        autoFocus
                      />
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleSaveEdit(note)}
                          disabled={savingEdit || !editDraft.trim()}
                          className="btn btn-primary btn-xs gap-1"
                        >
                          <Check className="w-3.5 h-3.5" />
                          {savingEdit ? 'Saving…' : 'Save'}
                        </button>
                        <button
                          type="button"
                          onClick={handleCancelEdit}
                          disabled={savingEdit}
                          className="btn btn-ghost btn-xs gap-1"
                        >
                          <X className="w-3.5 h-3.5" />
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-text-primary whitespace-pre-wrap min-w-0 flex-1">
                      {note.body}
                    </p>
                  )}

                  {!isEditing && (
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {canCreate && (
                        <>
                          <button
                            type="button"
                            onClick={() => handleTogglePin(note)}
                            disabled={isPinning}
                            className={`btn btn-ghost btn-sm btn-square ${
                              note.isPinned
                                ? 'text-amber-600 dark:text-amber-400'
                                : 'text-text-tertiary'
                            }`}
                            title={note.isPinned ? 'Unpin note' : 'Pin note to top'}
                          >
                            {note.isPinned ? (
                              <PinOff className="w-4 h-4" />
                            ) : (
                              <Pin className="w-4 h-4" />
                            )}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleStartEdit(note)}
                            className="btn btn-ghost btn-sm btn-square text-text-tertiary"
                            title="Edit note"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                      {canDelete && (
                        <button
                          type="button"
                          onClick={() => handleDelete(note)}
                          className="btn btn-ghost btn-sm btn-square text-text-tertiary hover:text-error"
                          title="Delete note"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2 mt-3 text-sm text-text-tertiary">
                  <User className="w-3.5 h-3.5" />
                  <span>{note.authorName || 'Unknown'}</span>
                  <span aria-hidden="true">·</span>
                  <span>{formatTimestamp(note.createdAt)}</span>
                  {isEdited && <span className="italic text-text-tertiary">(edited)</span>}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
